import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberCategoryClient, MemberCategoryDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberCategoryClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberCategories = () => {
    return useQuery({
        queryKey: ['memberCategories'],
        queryFn: async () => {
            const response = await apiClient.searchMemberCategories({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useMemberCategorySearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchMemberCategories(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useMemberCategoryById = (id: string) => {
    return useQuery({
        queryKey: ['memberCategory', id],
        queryFn: async () => {
            const response = await apiClient.getMemberCategoryById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateMemberCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MemberCategoryDTO) => {
            return await apiClient.createMemberCategory(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberCategories'] });
        },
    });
};

// Update mutation
export const useUpdateMemberCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MemberCategoryDTO }) => {
            return await apiClient.updateMemberCategory(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberCategories'] });
        },
    });
};

// Delete mutation
export const useDeleteMemberCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteMemberCategory(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberCategories'] });
        },
    });
};